import { useEffect, useMemo, useRef } from 'react';
import { useResizeObserver } from '../../../hooks/useResizeObserver.ts';
import { trpc } from '../../../utils/trpc/trpc.ts';
import { drawReportGraph } from '../drawReportGraph.ts';
import { ReportSettings } from '../report-settings/reportSettings.ts';
import { ReportGraphContainer } from '../reportGraphContainer.tsx';
import { prepareMeasurementCounts } from './prepareMeasurementCounts.ts';


interface Props {
    style?: React.CSSProperties;
    reportSettings: ReportSettings;
    operatorFooterLabelsByPeriod?: string[][];
    onOperatorFooterClick?: (periodIndex: number) => void;
}

export function MeasurementCountsGraph({
    style,
    reportSettings,
    operatorFooterLabelsByPeriod,
    onOperatorFooterClick,
}: Props) {
    const graphContainerRef = useRef<HTMLDivElement>(null);
    const dimensions = useResizeObserver(graphContainerRef);

    /* A bit weird to use measurement delays to count the measurements, but we are already getting
    that data for other graphs and the counts should match up. */
    const { data: delaysData, isFetching } = trpc.graphsData.getMeasurementDelays.useQuery({
        measurementTime: {
            start: reportSettings.first.start,
            end: reportSettings.last.end,
        }
    }, { refetchOnWindowFocus: false, });


    const preparedData = useMemo(() => {
        if (!delaysData) return undefined;
        return prepareMeasurementCounts(delaysData, reportSettings);
    }, [delaysData, reportSettings]);

    useEffect(() => {
        if (!graphContainerRef.current || !preparedData) return;
        drawReportGraph(graphContainerRef.current!, preparedData, operatorFooterLabelsByPeriod, onOperatorFooterClick);
    }, [graphContainerRef, preparedData, operatorFooterLabelsByPeriod, onOperatorFooterClick, dimensions]);


    return (
        <ReportGraphContainer
            style={{
                ...style,
                height: '100%',
            }}
            containerRef={graphContainerRef}
            isLoading={isFetching}
        />
    )
}
